import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { EyeCloseIcon, EyeOpenIcon } from "@/assets/icons";
import { MapContextType } from "@/context/MapProvider";
import { useMap } from "@/hooks/map/core/useMapContext";
import { RootState } from "@/store";
import {
  LegendFilterType,
  MapState,
  setLegendFilters
} from "@/store/slice/mapSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  ColorRanges,
  DataSchemaType,
  Dataset,
  DatasetData,
  DatasetDataRowValue
} from "@/types/dataset";
import { getAvailableDatasets, isOrdinalType } from "@/utils/dataset";
import { getBeforeId } from "@/utils/map";
import { formatRenderValue } from "@/utils/string";

import { twMerge } from "tailwind-merge";

interface LegendInfoProps {
  filterLegends: MapState["legendFilters"];
  setFilterLegends: (val: { [datasetId: string]: LegendFilterType[] }) => void;
}

interface LegendItemProps extends LegendInfoProps {
  range: ColorRanges;
  dataType: DataSchemaType;
  id: string;
}

const LegendItem = ({
  range,
  dataType,
  id,
  filterLegends,
  setFilterLegends
}: LegendItemProps) => {
  const { label, color, value, min, max } = range;

  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";

  const isVisible = useMemo(() => {
    const list = filterLegends?.[id] ?? [];

    if (value !== undefined) return !list.includes(value);

    if (min !== undefined && max !== undefined) {
      return !list.some(
        (v) => typeof v === "object" && v?.min === min && v?.max === max
      );
    }

    return !list.includes("others");
  }, [filterLegends, id, value, min, max]);

  const getFormattedLabel = (): string => {
    if (dataType === DataSchemaType.INT || dataType === DataSchemaType.FLOAT) {
      return String(label);
    }

    if (label === "" || label === "null") {
      return "null";
    }

    const formattedLabel = formatRenderValue(dataType, label);
    return typeof formattedLabel === "string"
      ? formattedLabel
      : String(formattedLabel);
  };

  const handleToggleVisibility = () => {
    const current = filterLegends?.[id] ?? [];
    let item;
    if (value !== undefined) {
      item = value;
    } else if (min !== undefined && max !== undefined) {
      item = { min, max };
    } else {
      item = "others";
    }

    const updated = isVisible
      ? [...current, item]
      : current.filter((v) => {
          if (typeof item === "string") {
            return v !== item;
          }
          return !(
            typeof v === "object" &&
            v !== null &&
            v.min === item?.min &&
            v.max === item.max
          );
        });

    setFilterLegends({
      ...filterLegends,
      [id]: updated
    });
  };

  return (
    <li
      data-testid="legend-item"
      className={twMerge(
        "flex flex-row items-center justify-between gap-2 mb-1 p-1 rounded-[4px]",
        !isPreviewMode ? "hover:bg-[#E6F4F2] cursor-pointer group" : ""
      )}
    >
      <div className="flex flex-row items-center gap-1">
        <div
          className="h-4 w-4 min-w-4 rounded-sm"
          style={{
            backgroundColor: getFormattedLabel() === "null" ? "#CECECE" : color
          }}
        />
        <div
          className={`text-[10px] ${isVisible ? "text-txtBody" : "text-light-gray"}`}
          data-testid="legend-item-label"
        >
          {getFormattedLabel()}
        </div>
      </div>

      {!isPreviewMode && (
        <span
          className={isVisible ? "invisible group-hover:visible" : "visible"}
        >
          {isVisible ? (
            <EyeOpenIcon
              className="fill-dim-gray"
              onClick={handleToggleVisibility}
              data-testid="EyeOpenIcon"
            />
          ) : (
            <EyeCloseIcon
              className="fill-dim-gray"
              onClick={handleToggleVisibility}
              data-testid="EyeCloseIcon"
            />
          )}
        </span>
      )}
    </li>
  );
};

/* PRD:
  For numeric data, it can always be divided into 10 intervals. 
  However, if the data is non-numeric and contains more than 10 unique categories, the 10th legend will be labeled as "Others".
  But if there are exactly 10 unique categories, then all will be shown as-is, and the 10th legend will not be labeled as "Others".
*/
const DatasetLegend = ({
  dataset,
  rawDataset,
  filterLegends,
  setFilterLegends,
  handleFilterLayerByLegend,
  order
}: {
  dataset: Dataset;
  rawDataset: DatasetData;
  handleFilterLayerByLegend: MapContextType["handleFilterLayerByLegend"];
  order: { id: string; type: SpatialAggregationType }[];
} & LegendInfoProps) => {
  const originalColorRanges = dataset.style.colorRanges;

  const attributeName =
    dataset.properties.spatialType === "line"
      ? dataset.style.stroke?.attribute_name
      : dataset.selectedCategory.attribute_name;

  const rowIndex = rawDataset?.columns?.findIndex(
    (col) => col.name === attributeName
  );

  let rowValues: Exclude<DatasetDataRowValue, null>[] = [];
  if (rowIndex !== -1) {
    rowValues = rawDataset?.rows?.map(
      (rowData) => rowData[rowIndex] ?? undefined
    );
  }
  const rawDatasetDataUnique = [...new Set(rowValues)];
  const dataSize = rawDatasetDataUnique?.length ?? 0;

  const dataType =
    dataset.properties.spatialType === "line"
      ? (dataset.categories.find((c) => c.attribute_name === attributeName)
          ?.data_type ?? dataset.selectedCategory.data_type)
      : dataset.selectedCategory.data_type;
  // Check if the data type is non-numeric/ordinal
  const isOrdinal = isOrdinalType(dataType);

  let displayedRanges = originalColorRanges;

  const nullRanges = originalColorRanges.filter(
    (r) => r.value === "" || r.value === "null"
  );

  const nonNullRanges = originalColorRanges.filter(
    (r) => r.value !== "" && r.label?.toString().toLowerCase() !== "null"
  );

  const colorRangeLength = nonNullRanges.length;

  const shouldAddOthers =
    isOrdinal &&
    dataType !== DataSchemaType.BOOL &&
    dataSize > colorRangeLength;

  if (shouldAddOthers) {
    const topRanges = nonNullRanges.slice(0, colorRangeLength - 1);
    const othersRange = {
      color: nonNullRanges[colorRangeLength - 1]?.color,
      label: "Others"
    };
    displayedRanges = [...topRanges, othersRange];
  } else {
    displayedRanges = nonNullRanges;
  }

  // always append null ranges at the end (after Others if it exists)
  displayedRanges = [...displayedRanges, ...nullRanges];

  const previousLayerId = useMemo(
    () => getBeforeId(dataset.data.id, order),
    [dataset.data.id, order]
  );

  return (
    <div className="flex flex-col relative" key={dataset.data.id}>
      <div className="mb-2">
        <h2 className="font-semibold text-xs text-txtHeading sticky top-0">
          {dataset.data.display_name}
        </h2>
        <span className="text-[10px]">{attributeName}</span>
      </div>
      {displayedRanges.map((range, index) => (
        <LegendItem
          key={`${range.label || range.min}-${index}`}
          dataType={dataType}
          range={range}
          filterLegends={filterLegends}
          setFilterLegends={(val) => {
            setFilterLegends(val);
            handleFilterLayerByLegend(
              val[dataset.data.id],
              dataset,
              rawDataset,
              previousLayerId
            );
          }}
          id={dataset.data.id}
        />
      ))}
    </div>
  );
};

export function LegendsInfo() {
  const dispatch = useDispatch();
  const { handleFilterLayerByLegend } = useMap();
  const { datasets, visualizationOrder, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const availableDatasets = getAvailableDatasets(datasets);
  const { isExpandLegendVisualization } = useSelector(
    (state: RootState) => state.ui
  );
  const { legendFilters } = useSelector((state: RootState) => state.map);

  // Order datasets according to visualizationOrder of available datasets
  const orderedDatasets = visualizationOrder
    .filter((datasetId) =>
      Object.prototype.hasOwnProperty.call(availableDatasets, datasetId)
    )
    .map((datasetId) => availableDatasets[datasetId])
    .filter((item) => item.properties.isVisible);

  return (
    <div
      className={twMerge(
        "absolute bottom-[24px] h-fit max-h-[75%] border-gainsboro right-24 z-50 bg-white px-2 rounded-lg overflow-auto",
        isExpandLegendVisualization ? "w-[522px]" : "w-[168px]"
      )}
    >
      <h1 className="sticky top-0 font-semibold text-base text-txtHeading py-2 bg-white z-10">
        Legends
      </h1>
      {orderedDatasets.length === 0 && (
        <div className="mt-2 text-xs text-txtBody">
          No legend data available. Please add layers to view relevant
          information.
        </div>
      )}
      <div
        className={twMerge(
          "my-2 max-h-[80vh]",
          isExpandLegendVisualization ? "flex" : ""
        )}
      >
        <div className="flex flex-col gap-2">
          {orderedDatasets.map((dataset) => (
            <DatasetLegend
              key={dataset.data.id}
              dataset={dataset}
              rawDataset={rawDatasets?.[dataset.data.id ?? ""]}
              filterLegends={legendFilters}
              setFilterLegends={(val) => {
                dispatch(setLegendFilters(val));
              }}
              handleFilterLayerByLegend={handleFilterLayerByLegend}
              order={orderedDatasets.map((v) => ({
                id: v.data.id,
                type: v.data.spatial_aggregation_type
              }))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default LegendsInfo;
